import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Toaster } from "sonner";

import PublicLayout from "@/components/PublicLayout";
import DashboardLayout from "@/components/DashboardLayout";
import RequireAuth from "@/components/RequireAuth";
import CookieBanner from "@/components/CookieBanner";
import LegalReacceptGate from "@/components/LegalReacceptGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import InstallPrompt from "@/components/InstallPrompt";
import ScrollToTop from "@/components/ScrollToTop";
import LocalDevBadge from "@/components/LocalDevBadge";

import Home from "@/pages/Home";
import Features from "@/pages/Features";
import FeaturesRenters from "@/pages/FeaturesRenters";
import FeaturesBusinesses from "@/pages/FeaturesBusinesses";
import Businesses from "@/pages/Businesses";
import SalesAgentCareers from "@/pages/SalesAgentCareers";
import RecruitmentDashboard from "@/pages/RecruitmentDashboard";
import BookingIntakeExplainer from "@/pages/BookingIntakeExplainer";
import BusinessPricing from "@/pages/BusinessPricing";
import CustomerPricing from "@/pages/CustomerPricing";
import DiscoverRentals from "@/pages/DiscoverRentals";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import FindBooking from "@/pages/FindBooking";
import RequestConfirmation from "@/pages/RequestConfirmation";
import CustomerConfirmation from "@/pages/CustomerConfirmation";
import ClaimBusiness from "@/pages/ClaimBusiness";
import Dashboard from "@/pages/Dashboard";
import Bookings from "@/pages/Bookings";
import BookingDetail from "@/pages/BookingDetail";
import BookingTimelinePage from "@/pages/BookingTimelinePage";
import MiniSiteLayout from "@/pages/MiniSiteLayout";
import MiniSiteHome from "@/pages/minisite/Home";
import MiniSiteFleet from "@/pages/minisite/Fleet";
import MiniSiteBooking from "@/pages/minisite/Booking";
import MiniSiteAbout from "@/pages/minisite/About";
import MiniSitePolicies from "@/pages/minisite/Policies";
import MiniSiteFAQ from "@/pages/minisite/FAQ";
import MiniSiteReviews from "@/pages/minisite/Reviews";
import MiniSiteContact from "@/pages/minisite/Contact";
import BookingNew from "@/pages/BookingNew";
import Vehicles from "@/pages/Vehicles";
import VehicleImport from "@/pages/VehicleImport";
import VehicleDetail from "@/pages/VehicleDetail";
import CustomerProfile from "@/pages/CustomerProfile";
import Customers from "@/pages/Customers";
import FleetCalendar from "@/pages/FleetCalendar";
import VehicleDamages from "@/pages/VehicleDamages";
import Administration from "@/pages/Administration";
import FinanceBooks from "@/pages/FinanceBooks";
import ComplianceLegalReadiness from "@/pages/ComplianceLegalReadiness";
import InAppBookingSettings from "@/pages/InAppBookingSettings";
import PublicBusiness from "@/pages/PublicBusiness";
import PublicScenariosIndex from "@/pages/public/PublicScenariosIndex";
import PublicScenarioStage from "@/pages/public/PublicScenarioStage";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import BusinessLocationSettings from "@/pages/settings/BusinessLocationSettings";
import InspectionChecklistSettings from "@/pages/settings/InspectionChecklistSettings";
import TermsSettings from "@/pages/settings/TermsSettings";
import CommunicationSettings from "@/pages/settings/CommunicationSettings";
import EnhancedSafetySettings from "@/pages/settings/EnhancedSafetySettings";
import FleetPerformance from "@/pages/FleetPerformance";
import PublicProfile from "@/pages/PublicProfile";
import PublicWebsiteSettings from "@/pages/PublicWebsiteSettings";
import WebsiteIntegrations from "@/pages/WebsiteIntegrations";
import HostedBooking from "@/pages/HostedBooking";
import EmbeddedBooking from "@/pages/EmbeddedBooking";
import WalkthroughStageFrame from "@/components/walkthrough2/WalkthroughStageFrame";
import Inquiries from "@/pages/Inquiries";
import PartnerRewards from "@/pages/PartnerRewards";
import VerificationRequests from "@/pages/VerificationRequests";
import Incidents from "@/pages/Incidents";
import IncidentNew from "@/pages/IncidentNew";
import IncidentDetail from "@/pages/IncidentDetail";
import Reminders from "@/pages/Reminders";
import Employees from "@/pages/Employees";
import Drivers from "@/pages/Drivers";
import DriverProfile from "@/pages/DriverProfile";
import DriverCalendar from "@/pages/DriverCalendar";
import DriverService from "@/pages/DriverService";
import DriverServiceProfile from "@/pages/DriverServiceProfile";
import DriverWorkspace from "@/pages/DriverWorkspace";
import DriverInvitation from "@/pages/DriverInvitation";
import RenterDriverPolicy from "@/pages/RenterDriverPolicy";
import RentalSettlementPolicy from "@/pages/RentalSettlementPolicy";
import RentalSettlement from "@/pages/RentalSettlement";
import Billing from "@/pages/Billing";
import Warnings from "@/pages/Warnings";
import ActivityFeed from "@/pages/ActivityFeed";
import StaffOnboarding from "@/pages/StaffOnboarding";
import StaffAvailability from "@/pages/StaffAvailability";
import StaffManagement from "@/pages/StaffManagement";
import BookingIntake from "@/pages/BookingIntake";
import BookingIntakeDraft from "@/pages/BookingIntakeDraft";
import IntakeChannels from "@/pages/IntakeChannels";
import StaffNotepadShell from "@/pages/StaffNotepadShell";
const StaffNotepad = lazy(() => import("@/pages/StaffNotepad"));

import CustomerLayout from "@/components/CustomerLayout";
import CustomerBookings from "@/pages/CustomerBookings";
import CustomerBookingDetail from "@/pages/CustomerBookingDetail";
import CustomerNotifications from "@/pages/CustomerNotifications";
import CustomerFindBusiness from "@/pages/CustomerFindBusiness";
import CustomerPrivacy from "@/pages/CustomerPrivacy";

// Admin + Promoter dashboards
import RequireRole from "@/components/RequireRole";
import AdminLayout from "@/components/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import CorporateFinance from "@/pages/admin/CorporateFinance";
import PlatformLegal from "@/pages/admin/PlatformLegal";
import LegalReadiness from "@/pages/admin/LegalReadiness";
import ClientCompliance from "@/pages/admin/ClientCompliance";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminOperations from "@/pages/admin/AdminOperations";
import BusinessWebsites from "@/pages/admin/BusinessWebsites";
import WebsiteRequests from "@/pages/admin/WebsiteRequests";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBannedEmails from "@/pages/admin/AdminBannedEmails";
import AdminPromoters from "@/pages/admin/AdminPromoters";
import AdminOutreachLogs from "@/pages/admin/AdminOutreachLogs";
import AdminPromoterTasks from "@/pages/admin/AdminPromoterTasks";
import AdminEmailPreview from "@/pages/admin/AdminEmailPreview";
import { AdminPlans, AdminSupport } from "@/pages/admin/AdminMisc";
import PromoterLayout from "@/components/PromoterLayout";
import PromoterOverview from "@/pages/promoter/PromoterOverview";
import PromoterNewLog from "@/pages/promoter/PromoterNewLog";
import PromoterMyLogs from "@/pages/promoter/PromoterMyLogs";
import PromoterMyTasks from "@/pages/promoter/PromoterMyTasks";
import PromoterMyPerformance from "@/pages/promoter/PromoterMyPerformance";
import PromoterSalesGuide from "@/pages/promoter/PromoterSalesGuide";
import PromoterDemosIndex from "@/pages/promoter/demos/PromoterDemosIndex";
import DemoStage from "@/pages/promoter/demos/DemoStage";

import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Support from "@/pages/Support";
import NotFound from "@/pages/NotFound";
import Academy from "@/pages/Academy";
import AcademyGuide from "@/pages/AcademyGuide";
import AcademyQuiz from "@/pages/AcademyQuiz";
import AcademyChecklist from "@/pages/AcademyChecklist";
import GamesHub from "@/pages/games/GamesHub";
import SpotTheIssueGame from "@/pages/games/SpotTheIssueGame";
import QuickQuizGame from "@/pages/games/QuickQuizGame";
import WordFinderGame from "@/pages/games/WordFinderGame";
import About from "@/pages/About";
import AboutBusiness from "@/pages/AboutBusiness";
import Investors from "@/pages/Investors";
import AdminAcademy from "@/pages/admin/AdminAcademy";import ReviewsDashboard from "@/pages/ReviewsDashboard";
import ReviewInvite from "@/pages/ReviewInvite";
import VehicleCheckPublic from "@/pages/VehicleCheckPublic";
import DailyChecklists from "@/pages/DailyChecklists";
import SupportTickets, { SupportTicketDetail } from "@/pages/SupportTickets";
import AdminSupportTickets from "@/pages/admin/AdminSupportTickets";
import AdminCompliance from "@/pages/admin/AdminCompliance";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminSupportAgents from "@/pages/admin/AdminSupportAgents";
import RequesterSupport from "@/pages/RequesterSupport";
import RequireSupportAgent from "@/components/RequireSupportAgent";
import SupportWorkspaceLayout from "@/components/SupportWorkspaceLayout";
import SupportWorkspaceCases from "@/pages/support-workspace/SupportWorkspaceCases";
import SupportWorkspaceKnowledge from "@/pages/support-workspace/SupportWorkspaceKnowledge";
import AcceptSupportInvite from "@/pages/support-workspace/AcceptInvite";
import {
  TermsPage, PrivacyPage, CookiesPage, RefundPage, DisclaimerPage,
  AcceptableUsePage, BusinessTermsPage, TrustSafetyPage,
  CustomerTermsPage, DataDeletionPage, ReviewsPolicyPage,
  EnhancedSafetyPage,
} from "@/pages/legal";
// ConfirmPH's public legal surface: the Policy Centre, what ConfirmPH
// actually verifies, the security contact, and the four distinct reporting
// routes. These read the platform compliance registers rather than the
// static legal copy, so a public page can never claim something that has
// not been recorded and verified.
import PolicyCentre from "@/pages/legal/PolicyCentre";
import PublishedPolicy from "@/pages/legal/PublishedPolicy";
import ArchivedPolicy from "@/pages/legal/ArchivedPolicy";
import WhatWeVerify from "@/pages/legal/WhatWeVerify";
import SecurityContact from "@/pages/legal/SecurityContact";
import ReportCentre from "@/pages/legal/ReportCentre";

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <I18nProvider>
          <AuthProvider>
            <BrowserRouter>
            <ScrollToTop />
            <Toaster richColors position="top-right" />
            <LocalDevBadge />
            <CookieBanner />
            <LegalReacceptGate />
            <InstallPrompt />
            <Routes>
              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/features" element={<Features />} />
                <Route path="/features/renters" element={<FeaturesRenters />} />
                <Route path="/features/businesses" element={<FeaturesBusinesses />} />
                <Route path="/businesses" element={<Businesses />} />
                <Route path="/careers" element={<SalesAgentCareers />} />
                <Route path="/careers/sales-agent" element={<SalesAgentCareers />} />
                <Route path="/businesses/intake" element={<BookingIntakeExplainer />} />
                <Route path="/businesses/pricing" element={<BusinessPricing />} />
                <Route path="/pricing" element={<CustomerPricing />} />
                <Route path="/rentals" element={<DiscoverRentals />} />
                <Route path="/academy" element={<Academy audience="renter" />} />
                <Route path="/businesses/academy" element={<Academy audience="business" />} />
                <Route path="/academy/guide/:slug" element={<AcademyGuide />} />
                <Route path="/academy/quiz/:slug" element={<AcademyQuiz />} />
                <Route path="/academy/checklists/:slug" element={<AcademyChecklist />} />
                <Route path="/academy/games" element={<GamesHub />} />
                <Route path="/academy/games/spot-the-issue" element={<SpotTheIssueGame />} />
                <Route path="/academy/games/quick-quiz" element={<QuickQuizGame />} />
                <Route path="/academy/games/word-finder" element={<WordFinderGame />} />
                <Route path="/about" element={<About />} />
                <Route path="/businesses/about" element={<AboutBusiness />} />
                <Route path="/find-booking" element={<FindBooking />} />
                <Route path="/request-confirmation" element={<RequestConfirmation />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />

                {/* Legal / info / support pages */}
                <Route path="/terms"          element={<TermsPage />} />
                <Route path="/privacy"        element={<PrivacyPage />} />
                <Route path="/cookies"        element={<CookiesPage />} />
                <Route path="/refund-policy"  element={<RefundPage />} />
                <Route path="/disclaimer"     element={<DisclaimerPage />} />
                <Route path="/acceptable-use" element={<AcceptableUsePage />} />
                <Route path="/business-terms" element={<BusinessTermsPage />} />
                <Route path="/customer-terms" element={<CustomerTermsPage />} />
                <Route path="/trust-safety"   element={<TrustSafetyPage />} />
                <Route path="/enhanced-safety" element={<EnhancedSafetyPage />} />
                <Route path="/data-deletion"  element={<DataDeletionPage />} />
                <Route path="/reviews-policy" element={<ReviewsPolicyPage />} />
                <Route path="/legal"          element={<PolicyCentre />} />
                <Route path="/legal/policies/:docType" element={<PublishedPolicy />} />
                <Route path="/legal/archive/:slug/:version" element={<ArchivedPolicy />} />
                <Route path="/what-we-verify" element={<WhatWeVerify />} />
                <Route path="/security"       element={<SecurityContact />} />
                <Route path="/report"         element={<ReportCentre />} />
                <Route path="/faq"            element={<Faq />} />
                <Route path="/contact"        element={<Contact />} />
                <Route path="/support"        element={<Support />} />
              </Route>

              {/* Standalone public token pages (no header) */}
              {/* Investor page owns its own minimal header/footer (see
                  Investors.jsx) rather than the full public nav: kept
                  outside PublicLayout so it stays isolated and simple. */}
              <Route path="/investors" element={<Investors />} />
              <Route path="/confirm/:token" element={<CustomerConfirmation />} />
              <Route path="/driver-invitation/:token" element={<DriverInvitation />} />
              {/* Task 7: the after-the-rental account. Its own route because the
                  confirmation page refuses a link once the booking is finished. */}
              <Route path="/rental-settlement/:token" element={<RentalSettlement />} />
              <Route path="/business/:companyId" element={<PublicBusiness />} />
              <Route path="/claim/:token" element={<ClaimBusiness />} />
              <Route path="/review/:token" element={<ReviewInvite />} />
              <Route path="/vehicle-check" element={<VehicleCheckPublic />} />
              <Route path="/vehicle-check/:token" element={<VehicleCheckPublic />} />
              <Route path="/book/:businessSlug" element={<HostedBooking />} />
              <Route path="/embed/v1/book/:integrationId" element={<EmbeddedBooking />} />
              {/* Unlisted: the homepage walkthrough's own Phone-mode iframe target
                  (see components/walkthrough2/DeviceFrame.jsx). Not part of any
                  nav, never crawled/linked; renders only in response to real
                  postMessage state from the parent window. */}
              <Route path="/embed/renter-walkthrough" element={<WalkthroughStageFrame />} />

              {/* Public "Try ConfirmPH" scenarios: anonymous, sandboxed.
                  Reuses /promoter/demos scenario engine but with the
                  business-owner presentation layer.  See PublicScenariosIndex. */}
              <Route path="/demo-business/scenarios" element={<PublicScenariosIndex />} />
              <Route path="/demo-business/scenarios/:scenarioId" element={<PublicScenarioStage />} />

              {/* Enterprise multi-page mini-site (own sticky nav + footer).
                  Non-Enterprise businesses still get the legacy single-page
                  profile via the internal layout switch. */}
              <Route path="/rentals/:slug" element={<MiniSiteLayout />}>
                <Route index element={null} />
                <Route path="home"     element={<MiniSiteHome />} />
                <Route path="fleet"    element={<MiniSiteFleet />} />
                <Route path="booking"  element={<MiniSiteBooking />} />
                <Route path="about"    element={<MiniSiteAbout />} />
                <Route path="policies" element={<MiniSitePolicies />} />
                <Route path="faq"      element={<MiniSiteFAQ />} />
                <Route path="reviews"  element={<MiniSiteReviews />} />
                <Route path="contact"  element={<MiniSiteContact />} />
              </Route>

              {/* Local Mode notepad: deliberately outside authentication and
                  the business dashboard. Import is authenticated separately.
                  Canonical route: /notepad (its own scope so the PWA can be
                  installed alongside the Dashboard PWA on Android Chrome).
                  /app/notepad is kept as a redirect for any old links. */}
              <Route path="/notepad"
                      element={
                        <Suspense fallback={<StaffNotepadShell />}>
                          <StaffNotepad />
                        </Suspense>
                      } />
              <Route path="/app/notepad" element={<Navigate to="/notepad" replace />} />

              {/* Protected dashboard */}
              <Route path="/app"
                      element={<RequireAuth><DashboardLayout /></RequireAuth>}>
                <Route index element={<Dashboard />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="bookings/new" element={<BookingNew />} />
                <Route path="bookings/:id" element={<BookingDetail />} />
                <Route path="bookings/:id/timeline" element={<BookingTimelinePage />} />
                <Route path="booking-intake" element={<BookingIntake />} />
                <Route path="booking-intake/drafts/:id" element={<BookingIntakeDraft />} />
                <Route path="booking-intake/channels" element={<IntakeChannels />} />
                <Route path="vehicles" element={<Vehicles />} />
                <Route path="vehicles/import" element={<VehicleImport />} />
                <Route path="vehicles/:id" element={<VehicleDetail />} />
                <Route path="vehicles/:id/damages" element={<VehicleDamages />} />
                <Route path="fleet-calendar" element={<FleetCalendar />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:email" element={<CustomerProfile />} />
                <Route path="administration" element={<Administration />} />
                <Route path="finance" element={<FinanceBooks />} />
                <Route path="compliance" element={<ComplianceLegalReadiness />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="requests" element={<VerificationRequests />} />
                <Route path="incidents" element={<Incidents />} />
                <Route path="incidents/new" element={<IncidentNew />} />
                <Route path="incidents/:id" element={<IncidentDetail />} />
                <Route path="warnings" element={<Warnings />} />
                <Route path="activity" element={<ActivityFeed />} />
                <Route path="onboarding" element={<StaffOnboarding />} />
                <Route path="reminders" element={<Reminders />} />
                <Route path="employees" element={<Employees />} />
                <Route path="drivers" element={<Drivers />} />
                <Route path="drivers/calendar" element={<DriverCalendar />} />
                <Route path="drivers/:id" element={<DriverProfile />} />
                <Route path="driver-service" element={<DriverService />} />
                <Route path="driver-service/drivers/:id" element={<DriverServiceProfile />} />
                <Route path="driver" element={<DriverWorkspace />} />
                <Route path="renter-drivers" element={<RenterDriverPolicy />} />
                <Route path="rental-settlement" element={<RentalSettlementPolicy />} />
                <Route path="staff" element={<StaffAvailability />} />
                <Route path="staff-management" element={<StaffManagement />} />
                <Route path="billing" element={<Billing />} />
                <Route path="settings" element={<Settings />} />
                <Route path="analytics/fleet" element={<FleetPerformance />} />
                {/* /app/recruitment removed (Phase 2J consistency sweep): this was
                    an exact duplicate of /admin/recruitment with the same
                    component and a single hardcoded-email gate. Platform-admin
                    tooling belongs under /admin, not a business owner's nav. */}
                <Route path="settings/public-profile" element={<PublicProfile />} />
                <Route path="settings/public-website" element={<PublicWebsiteSettings />} />
                <Route path="settings/website-integrations" element={<WebsiteIntegrations />} />
                <Route path="settings/location" element={<BusinessLocationSettings />} />
                <Route path="settings/checklists" element={<InspectionChecklistSettings />} />
                <Route path="settings/terms" element={<TermsSettings />} />
                <Route path="settings/communications" element={<CommunicationSettings />} />
                <Route path="settings/in-app-bookings" element={<InAppBookingSettings />} />
                <Route path="settings/enhanced-safety" element={<EnhancedSafetySettings />} />
                <Route path="inquiries" element={<Inquiries />} />
                <Route path="reviews" element={<ReviewsDashboard />} />
                <Route path="checklists" element={<DailyChecklists />} />
                <Route path="support" element={<SupportTickets />} />
                <Route path="support/:id" element={<SupportTicketDetail />} />
                {/* Support V1 requester surface: deliberately at /app/help,
                    not /app/support (that prefix already belongs to the
                    pre-existing business<->platform-admin ticket channel
                    above). Any authenticated business identity may use it,
                    same as the rest of /app. */}
                <Route path="help" element={<RequesterSupport />} />
                <Route path="partner-rewards" element={<PartnerRewards />} />
              </Route>

              {/* Customer portal */}
              <Route path="/portal"
                      element={<RequireAuth customerOnly><CustomerLayout /></RequireAuth>}>
                <Route index element={<CustomerBookings />} />
                <Route path="bookings/:id" element={<CustomerBookingDetail />} />
                <Route path="notifications" element={<CustomerNotifications />} />
                <Route path="find" element={<CustomerFindBusiness />} />
                <Route path="privacy" element={<CustomerPrivacy />} />
                <Route path="support" element={<RequesterSupport />} />
              </Route>

              {/* Support V1: support-agent workspace. Deliberately its own
                  top-level route section (not nested under /app) because a
                  support agent is a platform-level actor layered on top of
                  whatever account they already have (business owner,
                  employee, platform_admin, or a dedicated invited identity).
                  RequireAuth's business/customer/platform_admin branching
                  can't express that, so RequireSupportAgent does a live
                  capability check instead. */}
              <Route path="/support-workspace/accept-invite" element={<AcceptSupportInvite />} />
              <Route path="/support-workspace"
                      element={<RequireSupportAgent><SupportWorkspaceLayout /></RequireSupportAgent>}>
                <Route index element={<Navigate to="cases" replace />} />
                <Route path="cases" element={<SupportWorkspaceCases />} />
                <Route path="knowledge" element={<SupportWorkspaceKnowledge />} />
              </Route>

              {/* Super Admin (gated to SUPER_ADMIN_EMAIL only by backend) */}
              <Route path="/admin"
                      element={<RequireRole role="platform_admin"><AdminLayout /></RequireRole>}>
                <Route index element={<AdminOverview />} />
                <Route path="businesses" element={<AdminBusinesses />} />
                <Route path="corporate-finance" element={<CorporateFinance />} />
                <Route path="legal" element={<PlatformLegal />} />
                <Route path="legal-readiness" element={<LegalReadiness />} />
                <Route path="client-compliance" element={<ClientCompliance />} />
                <Route path="operations" element={<AdminOperations />} />
                <Route path="business-websites" element={<BusinessWebsites />} />
                <Route path="website-requests" element={<WebsiteRequests />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="plans" element={<AdminPlans />} />
                <Route path="banned-emails" element={<AdminBannedEmails />} />
                <Route path="promoters" element={<AdminPromoters />} />
                <Route path="outreach-logs" element={<AdminOutreachLogs />} />
                <Route path="promoter-tasks" element={<AdminPromoterTasks />} />
                <Route path="follow-up" element={<AdminOutreachLogs followUpOnly={true} />} />
                <Route path="academy" element={<AdminAcademy />} />
                <Route path="support" element={<AdminSupportTickets />} />
                <Route path="support-agents" element={<AdminSupportAgents />} />
                <Route path="compliance" element={<AdminCompliance />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="audit-logs" element={<AdminAuditLogs />} />
                <Route path="email-preview" element={<AdminEmailPreview />} />
                <Route path="recent-activity" element={<AdminSupport />} />
                <Route path="recruitment" element={<RecruitmentDashboard />} />
              </Route>

              {/* Promoter / field-promoter dashboard */}
              <Route path="/promoter"
                      element={<RequireRole role="promoter"><PromoterLayout /></RequireRole>}>
                <Route index element={<PromoterOverview />} />
                <Route path="guide" element={<PromoterSalesGuide />} />
                <Route path="demos" element={<PromoterDemosIndex />} />
                <Route path="demos/:scenarioId" element={<DemoStage />} />
                <Route path="new-log" element={<PromoterNewLog />} />
                <Route path="logs" element={<PromoterMyLogs />} />
                <Route path="tasks" element={<PromoterMyTasks />} />
                <Route path="performance" element={<PromoterMyPerformance />} />
              </Route>

              {/* 404: keeps URL so user knows what they typed */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          </AuthProvider>
        </I18nProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
